import React, { useState, useEffect, useCallback, useRef } from 'react';
import { momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt-br'; // Importar localização em português
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './timelineStyles.css'; // Importar estilos personalizados

// Importar componentes personalizados
import TimelineEvent from './TimelineEvent';
import MobileTimelineEvent from './MobileTimelineEvent';
import TimelineToolbar from './TimelineToolbar';
import TimelineResourceHeader from './TimelineResourceHeader';
import TimelineTimeSlotWrapper from './TimelineTimeSlotWrapper';
import TimeBlockingModal from './TimeBlockingModal';
import TimelineLegend from './TimelineLegend';
import UpcomingAppointments from './UpcomingAppointments';
import CalendarContainer from './CalendarContainer';
import ResourcePagination from './ResourcePagination';
import AppointmentDetailModal from '../../admin/AppointmentDetailModal';

// Importar hooks personalizados
import { 
  useTimelineEvents, 
  useTimelineResources, 
  useTimeBlocking,
  useTimelineNavigation,
  useTimelineTouchNavigation,
  useWindowSize,
  useUserPreferences
} from '../../../hooks';

// Importar utilitários
import { formatTimeDistance } from '../../../utils/formatTime';
import { useToast } from '../../ui/ToastManager';

// Configurar o moment para usar o locale português
moment.locale('pt-br');

// Usar o momentLocalizer para React Big Calendar
const localizer = momentLocalizer(moment);

// Definir as visualizações disponíveis - deve corresponder às visualizações do React Big Calendar
const CUSTOM_VIEWS = {
  day: true,
  week: true,
  month: true,
  agenda: true,
  // Visualizações com recursos
  ...(Views.RESOURCE_DAY ? { resourceDay: true } : {}),
  ...(Views.RESOURCE_WEEK ? { resourceWeek: true } : {})
};

/**
 * Componente de visualização de agenda usando React Big Calendar
 * Exibe agendamentos em formato de calendário responsivo
 * Suporta visualização por dia, semana e profissionais
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.appointments - Lista de agendamentos a serem exibidos
 * @param {string} props.selectedDate - Data inicial selecionada (formato YYYY-MM-DD)
 * @param {Array} props.professionals - Lista de profissionais
 * @param {Object} props.salonConfig - Configurações do salão
 * @param {Function} props.onEventUpdate - Callback quando um evento é atualizado
 * @param {Function} props.onTimeBlocked - Callback quando um horário é bloqueado
 * @param {Function} props.onViewChange - Callback quando a visualização é alterada
 * @returns {JSX.Element} - Elemento React
 */
const AppointmentTimeline = ({ 
  appointments = [], 
  selectedDate,
  professionals = [],
  salonConfig = {},
  onEventUpdate,
  onTimeBlocked,
  onViewChange
}) => {
  // Estado para mensagens de feedback
  const { showToast } = useToast();
  
  // Função de fallback para atualização de eventos quando onEventUpdate não é fornecido
  const handleEventUpdate = useCallback(async (appointmentId, updateData) => {
    // Mostra mensagem de feedback
    showToast('Função de atualização não disponível', 'warning');
    
    // Retorna false para indicar falha
    return false;
  }, [showToast]);
  
  // Usar a função fornecida ou o fallback
  const updateEvent = useCallback(async (appointmentId, updateData) => {
    if (typeof onEventUpdate === 'function') {
      try {
        const result = await onEventUpdate(appointmentId, updateData);
        return result;
      } catch (error) {
        showToast('Erro ao atualizar agendamento. Tente novamente.', 'error');
        return false;
      }
    } else {
      showToast('Função de atualização não disponível', 'warning');
      return false;
    }
  }, [onEventUpdate, showToast]);
  
  // Estado para controlar a data atual
  const [date, setDate] = useState(() => {
    if (selectedDate) {
      // Verificar se selectedDate é uma string ou um objeto Date
      if (typeof selectedDate === 'string') {
        // Se for string, dividir por hífen como antes
        const [year, month, day] = selectedDate.split('-').map(Number);
        return new Date(year, month - 1, day);
      } else if (selectedDate instanceof Date) {
        // Se for um objeto Date, usar diretamente
        return new Date(selectedDate);
      }
    }
    return new Date();
  });
  
  // Obter preferências do usuário
  const { preferences } = useUserPreferences();
  
  // Estado para controlar o número de colunas visíveis
  const [visibleColumns, setVisibleColumns] = useState(5);

  // Estado para controlar o índice inicial de recursos visíveis
  const [resourceStartIndex, setResourceStartIndex] = useState(0);
  
  // Estado para controlar a visualização atual do calendário
  const [currentView, setCurrentView] = useState(() => {
    // Verificar se 'day' está disponível, caso contrário usar a primeira visualização disponível
    if (CUSTOM_VIEWS.day) return 'day';
    return Object.keys(CUSTOM_VIEWS)[0] || 'day';
  });

  // Detectar o tamanho da janela usando o hook
  const { width } = useWindowSize();
  const isMobile = width < 768;

  // Referência para o container do calendário para manipulação de gestos e scroll
  const calendarContainerRef = useRef(null);

  // Ajustar o número de colunas visíveis baseado no tamanho da tela e preferências
  useEffect(() => {
    if (isMobile) {
      // No mobile, usar a preferência do usuário se definida
      const userMobileColumns = preferences.mobileColumns;
      if (userMobileColumns && userMobileColumns >= 1 && userMobileColumns <= 4) {
        setVisibleColumns(userMobileColumns);
      } else {
        // Fallback para as configurações padrão se não houver preferência válida
        const isIphone14ProMax = width >= 390 && width <= 430;
        
        if (width < 380) {
          setVisibleColumns(2); // Celulares muito pequenos - 2 colunas
        } else if (isIphone14ProMax || (width >= 380 && width < 768)) {
          setVisibleColumns(3); // iPhone 14 Pro/Pro Max e outros celulares médios - 3 colunas
        }
      }
    } else if (width < 1024) {
      setVisibleColumns(4); // Tablets
    } else {
      setVisibleColumns(5); // Desktop
    }
  }, [width, isMobile, preferences.mobileColumns]);

  // Usar hooks personalizados com interface compatível com o componente original
  const { resources, resourcesWithAny } = useTimelineResources({ professionals });

  // Usar hook para gerenciar navegação por toque
  const { 
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    navigateToNextResourceSet,
    navigateToPrevResourceSet
  } = useTimelineTouchNavigation({
    isMobile,
    resources: resourcesWithAny,
    visibleColumns,
    resourceStartIndex,
    setResourceStartIndex
  });
  
  // Usar o hook de navegação da timeline
  const { 
    handleDateNavigate,
    handleViewNavigate,
    navigateByView,
    goToToday
  } = useTimelineNavigation({ 
    date, 
    setDate,
    currentView,
    setCurrentView,
    customViews: CUSTOM_VIEWS,
    onDateChange: (newDate) => {
      // Implementar callback de mudança de data se necessário
    },
    onViewChange
  });
  
  // Função para mudar a visualização (manter compatibilidade com a API anterior)
  const changeView = useCallback((newView) => {
    if (CUSTOM_VIEWS[newView]) {
      setCurrentView(newView);
      // Chamar callback externo se fornecido
      if (typeof onViewChange === 'function') {
        onViewChange(newView);
      }
    } else {
      console.warn(`Visualização "${newView}" não está disponível, usando fallback.`);
      setCurrentView(CUSTOM_VIEWS.day ? 'day' : Object.keys(CUSTOM_VIEWS)[0]);
    }
  }, [onViewChange]);
  
  // Definir os rótulos para as visualizações do calendário
  const viewLabels = {
    day: 'Dia',
    week: 'Semana',
    month: 'Mês',
    agenda: 'Agenda',
    resourceDay: 'Por Profissional (Dia)',
    resourceWeek: 'Por Profissional (Semana)'
  };
  
  const {
    events, 
    eventStyleGetter,
    selectedEvent,
    handleSelectEvent,
    closeTooltip,
    isSaving,
    setIsSaving
  } = useTimelineEvents({ 
    appointments, 
    professionals,
    resources
  });
  
  const {
    blockModalOpen,
    blockingData,
    loading: blockLoading,
    setBlockModalOpen,
    updateBlockingField,
    applyTimeBlocking,
    openBlockingModal,
    activeTab,
    switchTab,
    allBlockings,
    loadingBlockings,
    editBlocking,
    removeBlocking,
    editMode
  } = useTimeBlocking({ 
    date,
    professionals,
    salonId: salonConfig.id // Passar salonId para o hook
  });
  
  // Atualizar a data quando selectedDate mudar (manter compatibilidade)
  useEffect(() => {
    if (selectedDate) {
      // Verificar se selectedDate é uma string ou um objeto Date
      if (typeof selectedDate === 'string') {
        // Se for string, dividir por hífen como antes
        const [year, month, day] = selectedDate.split('-').map(Number);
        setDate(new Date(year, month - 1, day));
      } else if (selectedDate instanceof Date) {
        // Se for um objeto Date, usar diretamente
        setDate(new Date(selectedDate));
      }
    }
  }, [selectedDate]);
  
  // Garantir que o calendário inicie no dia atual se não houver data selecionada
  useEffect(() => {
    if (!selectedDate) {
      setDate(new Date());
    }
  }, [selectedDate]);
  
  // Função para ajustar o número de colunas visíveis
  const handleColumnAdjustment = useCallback((columnCount) => {
    setVisibleColumns(columnCount);
    setResourceStartIndex(0); // Resetar o índice inicial ao mudar o número de colunas
  }, []);
  
  // Componentes personalizados para o calendário, separados por plataforma
  const components = {
    event: (props) => {
      const EventComponent = isMobile ? MobileTimelineEvent : TimelineEvent;
      return <EventComponent {...props} onSelect={handleSelectEvent} />;
    },
    toolbar: (props) => (
      <TimelineToolbar 
        {...props} 
        viewLabels={viewLabels}
        onColumnAdjust={handleColumnAdjustment}
        onOpenBlockModal={openBlockingModal}
        currentView={currentView}
        isMobile={isMobile}
      />
    ),
    resourceHeader: TimelineResourceHeader,
    timeSlotWrapper: TimelineTimeSlotWrapper
  };

  // Recursos visíveis com base no índice inicial e no número de colunas
  const visibleResources = resourcesWithAny.slice(
    resourceStartIndex, 
    resourceStartIndex + visibleColumns
  );

  // Verificar se há recursos antes ou depois dos visíveis
  const hasMoreLeft = resourceStartIndex > 0;
  const hasMoreRight = resourceStartIndex + visibleColumns < resourcesWithAny.length;

  // Definir altura do calendário com base no tamanho da tela
  const calendarHeight = 'auto'; // Altura automática para mostrar todo o conteúdo

  // Função personalizada para estilizar eventos (estilo Google Agenda em mobile)
  const customEventPropGetter = (event, start, end, isSelected) => {
    // Obter os estilos padrão do hook
    const defaultStyle = eventStyleGetter(event, start, end, isSelected);
    
    // Verificar se estamos em um dispositivo móvel
    const isMobile = width < 768;
    
    if (isMobile) {
      // Em dispositivos móveis, manter as cores originais mas aumentar a altura mínima
      return {
        ...defaultStyle,
        style: {
          ...defaultStyle.style,
          minHeight: '32px',
          padding: '2px 5px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          overflow: 'hidden', // Garantir que o conteúdo não ultrapasse os limites
          cursor: 'pointer', // Indicar que é clicável
          position: 'relative', // Para posicionamento adequado
          zIndex: 5, // Garantir que fique acima de outros elementos
        }
      };
    }
    
    // Retornar o estilo padrão para desktop
    return {
      ...defaultStyle,
      style: {
        ...defaultStyle.style,
        cursor: 'pointer', // Indicar que é clicável também no desktop
      }
    };
  };

  // Efeito para fechar o tooltip quando houver scroll no calendário
  useEffect(() => {
    function handleScroll() {
      if (selectedEvent) {
        closeTooltip();
      }
    }
    
    // Adicionar listener de scroll ao container do calendário e à janela
    const calendarContainer = calendarContainerRef.current;
    if (calendarContainer) {
      calendarContainer.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      if (calendarContainer) {
        calendarContainer.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [selectedEvent, closeTooltip]);

  useEffect(() => {
    if (isMobile) {
      // No mobile, preferimos uma visualização específica para melhor experiência
      const preferredMobileView = CUSTOM_VIEWS.resourceDay ? 'resourceDay' : 'day';
      changeView(preferredMobileView);
    }
  }, [isMobile, changeView, CUSTOM_VIEWS]);

  // Função para salvar bloqueio de horário
  const handleSaveBlocking = async () => {
    try {
      console.log("Iniciando salvamento de bloqueio");
      const savedBlocking = await applyTimeBlocking(salonConfig.id);
      
      if (savedBlocking && typeof onTimeBlocked === 'function') {
        // Chamar callback externo se fornecido
        try {
          await onTimeBlocked(savedBlocking);
        } catch (error) {
          console.error('Erro ao processar bloqueio:', error);
          showToast('Bloqueio salvo, mas ocorreu um erro ao atualizar a visualização', 'warning');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar bloqueio:', error);
      showToast('Erro ao salvar bloqueio de horário', 'error');
    }
  };

  // Preparar props para o componente Calendar
  const calendarProps = {
    localizer,
    events,
    startAccessor: "start",
    endAccessor: "end",
    resources: visibleResources,
    resourceIdAccessor: "id",
    resourceTitleAccessor: "title",
    step: 15,
    timeslots: 4,
    date,
    onNavigate: handleDateNavigate,
    view: currentView,
    onView: changeView,
    min: new Date(0, 0, 0, salonConfig?.horaInicio || 8, 0, 0),
    max: new Date(0, 0, 0, salonConfig?.horaFim || 20, 0, 0),
    defaultView: "day",
    views: CUSTOM_VIEWS,
    eventPropGetter: customEventPropGetter,
    components,
    onSelectEvent: handleSelectEvent,
    selectable: false,
    culture: "pt-BR",
    showMultiDayTimes: true,
    longPressThreshold: 10,
    drilldownView: "day",
    dayLayoutAlgorithm: "no-overlap",
    popup: true,
    formats: {
      timeGutterFormat: 'HH:mm',
      dayFormat: 'ddd DD/MM',
      dayHeaderFormat: 'dddd, DD [de] MMMM',
      dayRangeHeaderFormat: ({ start, end }) => 
        `${moment(start).format('DD/MM')} - ${moment(end).format('DD/MM')}`,
    },
    messages: {
      allDay: 'Dia Inteiro',
      previous: 'Anterior',
      next: 'Próximo',
      today: 'Hoje',
      month: 'Mês',
      week: 'Semana',
      day: 'Dia',
      agenda: 'Agenda',
      date: 'Data',
      time: 'Hora',
      event: 'Evento',
      noEventsInRange: 'Não há agendamentos neste período.'
    }
  };

  // Preparar handlers para gestos touch
  const touchHandlers = {
    onTouchStart,
    onTouchMove,
    onTouchEnd
  };

  return (
    <div className="mt-2 appointmentTimeline">
      {/* Calendário */}
      <CalendarContainer
        calendarProps={calendarProps}
        containerRef={calendarContainerRef}
        touchHandlers={touchHandlers}
        isMobile={isMobile}
        hasMoreLeft={hasMoreLeft}
        hasMoreRight={hasMoreRight}
        onPrevResource={navigateToPrevResourceSet}
        onNextResource={navigateToNextResourceSet}
        width={width}
        height={calendarHeight}
      />
      
      {/* Indicador de navegação (apenas em telas menores) */}
      {width < 1024 && resourcesWithAny.length > visibleColumns && (
        <ResourcePagination 
          totalCount={resourcesWithAny.length}
          visibleCount={visibleColumns}
          startIndex={resourceStartIndex}
          onPageChange={setResourceStartIndex}
        />
      )}
      
      {/* Próximos agendamentos - mostrar em todos os dispositivos */}
      <div className="bg-white rounded-lg shadow p-4 mt-4">
        <UpcomingAppointments 
          events={events} 
          resources={resourcesWithAny}
          onSelectAppointment={handleSelectEvent}
          formatTimeDistance={formatTimeDistance}
        />
      </div>
      
      {/* Legenda - apenas em desktop */}
      {!isMobile && (
        <div className="bg-white rounded-lg shadow p-4 mt-4">
          <TimelineLegend resources={resourcesWithAny} />
        </div>
      )}
      
      {/* Tooltip de detalhes de agendamento */}
      {selectedEvent && (
        <AppointmentDetailModal
          appointment={selectedEvent.appointment}
          isOpen={!!selectedEvent}
          onClose={closeTooltip}
          onReschedule={async (appointmentId, updateData) => {
            try {
              setIsSaving(true);
              const result = await updateEvent(appointmentId, {
                ...updateData,
                status: 'confirmado'
              });
              
              setIsSaving(false);
              
              if (result) {
                showToast('Agendamento remarcado com sucesso!', 'success');
              }
              
              closeTooltip();
              return result;
            } catch (error) {
              console.error('Erro ao remarcar agendamento:', error);
              setIsSaving(false);
              showToast('Erro ao remarcar agendamento. Tente novamente.', 'error');
              throw error;
            }
          }}
          onCancel={async (appointmentId) => {
            try {
              setIsSaving(true);
              // Preparar dados para cancelamento
              const result = await updateEvent(appointmentId, {
                status: 'cancelado',
                cancelado: true
              });
              
              setIsSaving(false);
              
              if (result) {
                showToast('Agendamento cancelado com sucesso!', 'success');
              }
              
              closeTooltip();
              return result;
            } catch (error) {
              console.error('Erro ao cancelar agendamento:', error);
              setIsSaving(false);
              showToast('Erro ao cancelar agendamento. Tente novamente.', 'error');
              throw error;
            }
          }}
          onEditServices={async (appointmentId, updateData) => {
            try {
              setIsSaving(true);
              // Preparar dados para atualização de serviços
              const result = await updateEvent(appointmentId, updateData);
              
              setIsSaving(false);
              
              if (result) {
                showToast('Serviços atualizados com sucesso!', 'success');
              }
              
              closeTooltip();
              return result;
            } catch (error) {
              console.error('Erro ao editar serviços:', error);
              setIsSaving(false);
              showToast('Erro ao atualizar serviços. Tente novamente.', 'error');
              throw error;
            }
          }}
          onMarkAsPaid={async (appointmentId, updateData = {}) => {
            try {
              setIsSaving(true);
              // Marcar como pago (usar apenas o campo pago, como no ConfirmationsPage)
              const result = await updateEvent(appointmentId, {
                pago: true,
                ...updateData  // Importante: isso permitirá incluir valorCustomizado do modal
              });
              
              setIsSaving(false);
              
              if (result) {
                showToast('Agendamento marcado como pago com sucesso!', 'success');
              }
              
              closeTooltip();
              return result;
            } catch (error) {
              console.error('Erro ao marcar como pago:', error);
              setIsSaving(false);
              showToast('Erro ao marcar como pago. Tente novamente.', 'error');
              throw error;
            }
          }}
        />
      )}
      
      {/* Modal de bloqueio de horário */}
      <TimeBlockingModal
        isOpen={blockModalOpen}
        blockingData={blockingData}
        onClose={() => setBlockModalOpen(false)}
        onSave={handleSaveBlocking}
        onChange={updateBlockingField}
        resources={professionals}
        loading={blockLoading}
        activeTab={activeTab}
        onTabChange={switchTab}
        blockings={allBlockings}
        loadingBlockings={loadingBlockings}
        onEdit={editBlocking}
        onDelete={removeBlocking}
        editMode={editMode}
        validationError={blockingData.validationError}
      />
    </div>
  );
};

export default AppointmentTimeline; 